import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAdminConsignment } from '../../services/admin/consignment.service';
import { ProductDA } from '../../services/admin/product.service';
import { Variant } from '../../models/variant.model';
import { GlobalSwal } from '../../styles/swal.style';
import { legendInvalid, legendValid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { Error } from '../../models/error.model';

import AppLegend from '../element/Legend';

export interface ModalConsignmentReceiveProps {
  companyForUser: any;
  consignment: any;
  storageCentersForCompany: any[];
  onReceived: () => void;
}

interface ReceiveLine {
  id_variant: string;
  product_name: string;
  unit_price: string;
  quantity: string;
}

let errorResponse: Error;

const ModalConsignmentReceive: React.FunctionComponent<ModalConsignmentReceiveProps> = ({ companyForUser, consignment, storageCentersForCompany, onReceived }) => {
  const { lang } = StateLanguage();

  const [loadIndicator, setLoadIndicator] = useState('off');
  const [storageCenter, setStorageCenter] = useState({ value: '', valid: false });
  const [lines, setLines] = useState<ReceiveLine[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [searchProduct, setSearchProduct] = useState('');

  const availableVariants = variants.filter((v) => {
    return !lines.some((l) => l.id_variant === v.id);
  });

  const filteredVariants = availableVariants.filter((v) => {
    if (!searchProduct) return true;
    const search = searchProduct.toLowerCase();
    return (v.display_name ?? '').toLowerCase().includes(search) ||
           (v.description_short ?? '').toLowerCase().includes(search) ||
           (v.code ?? '').toLowerCase().includes(search);
  });

  const handleChangeStorageCenter = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value.length > 0) {
      setStorageCenter({ value: event.target.value, valid: true });
      legendValid('modal-consignment-receive-legend-storage-center-required');
    } else {
      setStorageCenter({ value: '', valid: false });
      legendInvalid('modal-consignment-receive-legend-storage-center-required');
    }
  };

  const handleAddLine = (variant: Variant) => {
    setLines([...lines, {
      id_variant: variant.id,
      product_name: `${variant.display_name ?? ''}${variant.description_short ? ': ' + variant.description_short : ''}`,
      unit_price: variant.price_purchase?.amount?.toString() ?? '',
      quantity: '1'
    }]);
    setSearchProduct('');
  };

  const handleChangeUnitPrice = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    let newLines = [...lines];
    newLines[index].unit_price = event.target.value;
    setLines(newLines);
  };

  const handleChangeQuantity = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    let newLines = [...lines];
    newLines[index].quantity = event.target.value;
    setLines(newLines);
  };

  const removeLine = (index: number) => {
    let newLines = lines.filter((_, i) => i !== index);
    setLines(newLines);
  };

  const resetModal = () => {
    setStorageCenter({ value: '', valid: false });
    setLines([]);
    setSearchProduct('');
    legendValidInvalidRestart('modal-consignment-receive-legend-storage-center-required');
  };

  const executeHideModal = () => {
    modalHide('modal-consignment-receive');
    setTimeout(() => { resetModal(); }, 200);
  };

  const submitReceive = async () => {
    setLoadIndicator('on');

    let validLines = lines.length > 0 && lines.every(line => line.id_variant && parseFloat(line.unit_price) >= 0 && parseInt(line.quantity) > 0);

    if (companyForUser && consignment && storageCenter.valid && validLines) {
      let payload = lines.map(line => ({
        id_variant: line.id_variant,
        product_name: line.product_name,
        unit_price: parseFloat(line.unit_price),
        quantity: parseInt(line.quantity)
      }));

      await ApiDigitalAdminConsignment.postConsignmentReceive(companyForUser.id, consignment.id, storageCenter.value, payload).then((response) => {
        if (response.status === 200 || response.status === 201) {
          modalHide('modal-consignment-receive');
          setTimeout(() => { resetModal(); }, 200);

          GlobalSwal.fire({
            title: lang.labels.successfullyCreated,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 1800
          }).then(() => {
            onReceived();
          });
        } else {
          errorResponse = response.data;
          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          });
        }
      }).catch((error) => {
        errorWebHandler(lang, error);
      }).finally(() => {
        setLoadIndicator('off');
      });
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then(() => {
        if (!storageCenter.valid) legendInvalid('modal-consignment-receive-legend-storage-center-required');
        setLoadIndicator('off');
      });
    }
  };

  useEffect(() => {
    if (companyForUser) {
      ProductDA.getProductVariants(companyForUser.id, '', '', '', '').then((response) => {
        if (response.status === 200) {
          const data = response.data?.data ?? response.data;
          let items = data.items ?? data.product_variants ?? [];
          setVariants(items);
        }
      }).catch(() => {
        setVariants([]);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser]);

  return (
    <div id="modal-consignment-receive" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title">{lang.labels.receiveProducts}</h2>
            <button className="btn btn-sm btn-icon btn-active-color-primary" type="button" onClick={executeHideModal}>
              <i className="bi bi-x fs-1"></i>
            </button>
          </div>
          <div className="modal-body">
            <div className="row mb-5">
              <label className="form-label required">{lang.labels.storageCenter}</label>
              <div className="fv-row">
                <select className="form-select" name="storage_center" value={storageCenter.value} onChange={handleChangeStorageCenter}>
                  <option value="">{lang.labels.selectOption}</option>
                  {storageCentersForCompany.map((item: any, index: number) => (
                    <option key={index} value={item.id}>{item.name}</option>
                  ))}
                </select>
                <AppLegend attribute={{ check: storageCenter.valid, name: "modal-consignment-receive-storage-center", index: null, sub_index: null }} container={{ valid: false, required: true, identical: false, exists: false }}></AppLegend>
              </div>
            </div>
            <div className="table-responsive mb-3">
              <table className="table align-middle table-row-dashed mb-0">
                <thead className="border-bottom">
                  <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                    <th className="min-w-200px">{lang.labels.product}</th>
                    <th className="w-150px text-end">{lang.labels.priceUnit}</th>
                    <th className="w-100px text-end">{lang.labels.quantity}</th>
                    <th className="w-50px"></th>
                  </tr>
                </thead>
                <tbody className="text-secondary fw-bold">
                  {lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.product_name}</td>
                      <td>
                        <input className="form-control form-control-sm text-end" type="number" min="0" step="0.01" value={line.unit_price} onChange={(e) => handleChangeUnitPrice(index, e)} />
                      </td>
                      <td>
                        <input className="form-control form-control-sm text-end" type="number" min="1" step="1" value={line.quantity} onChange={(e) => handleChangeQuantity(index, e)} />
                      </td>
                      <td className="text-center">
                        <button className="btn btn-sm btn-icon btn-light-danger" type="button" onClick={() => removeLine(index)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {lines.length === 0 &&
                    <tr>
                      <td colSpan={4} className="text-center text-muted">{lang.labels.noRecordsFound}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <div className="fv-row">
              <Dropdown>
                <Dropdown.Toggle variant="btn btn-sm btn-light-primary w-100">
                  <i className="bi bi-plus-lg me-1"></i>
                  {lang.labels.addLine}
                </Dropdown.Toggle>
                <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-100 p-3 fs-7 mh-300px overflow-auto">
                  <div className="mb-2">
                    <input
                      className="form-control form-control-sm"
                      type="text"
                      placeholder={lang.labels.searchProduct}
                      value={searchProduct}
                      onChange={(e) => setSearchProduct(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  {filteredVariants.length > 0
                    ?
                    filteredVariants.slice(0, 50).map((item, index) => (
                      <Dropdown.Item key={index} bsPrefix="menu-item" onClick={() => handleAddLine(item)}>
                        <p className="menu-link fw-bold px-3 py-1 m-0">
                          {item.display_name}: {item.description_short}
                          {item.code ? ` [${item.code}]` : ''}
                        </p>
                      </Dropdown.Item>
                    ))
                    :
                    <div className="text-center text-muted py-3">{lang.labels.noRecordsFound}</div>
                  }
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-light me-3" type="button" onClick={executeHideModal}>{lang.labels.cancel}</button>
            <button className="btn btn-primary" type="button" data-kt-indicator={loadIndicator} onClick={submitReceive}>
              <span className="indicator-label">{lang.labels.receiveProducts}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalConsignmentReceive;
